import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import { Heading } from '../../ui/heading';

const V3Header: React.FC = () => {
  const location = useLocation();
  const { pageInfo, setPageInfo } = usePageHeader();
  const prevPathRef = useRef(location.pathname);

  // Clear pageInfo when route changes to prevent stale context values
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setPageInfo(null);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, setPageInfo]);

  // Use context pageInfo if available, otherwise determine based on route
  const getPageInfo = () => {
    if (pageInfo) {
      return pageInfo;
    }
    const path = location.pathname;

    if (path.includes('/v4/records')) {
      return {
        title: 'Conduct Records',
        description: 'Real-time intelligence on your customer feedback',
        hideHeader: false
      };
    } else if (path.includes('/v4/today')) {
      return {
        title: 'Today',
        description: 'Your daily briefing and activity summary',
        hideHeader: true
      };
    } else if (path.includes('/v3/cases')) {
      return {
        title: 'Resolution',
        description: 'Manage and track case resolution',
        hideHeader: false
      };
    } else if (path.includes('/v3/reporting')) {
      return {
        title: 'Reporting',
        description: 'Analytics and insights',
        hideHeader: false
      };
    } else if (path.includes('/v3/settings')) {
      return {
        title: 'Settings',
        description: 'Configure your workspace',
        hideHeader: false
      };
    } else if (path.includes('/v3/ombudsman')) {
      return {
        title: 'Case Guidance',
        description: 'Get guidance on regulatory outcomes for customer cases',
        hideHeader: false
      };
    } else if (path.includes('/v3/taxonomy')) {
      return {
        title: 'GO Conduct Intelligence Framework',
        description: 'Explore the classification taxonomy structure',
        hideHeader: false
      };
    }

    // Default
    return {
      title: 'Good Outcomes',
      description: 'Transform customer feedback into actionable insights',
      hideHeader: false
    };
  };
  
  const pageInfoData = getPageInfo();
  const { title, description, hideHeader } = pageInfoData;
  
  // If hideHeader is true, don't render the header
  if (hideHeader) {
    return null;
  }
  
  return (
    <header className="bg-white shadow-sm">
      <div className="px-6 h-20 flex items-center justify-between">
        {/* Left side - Page context */}
        <div className="flex flex-col">
          <Heading level={1} weight="bold" className="text-gray-900">{title}</Heading>
          <p className="text-sm text-gray-600">{description}</p>
        </div>

        {/* Right side - View Controls & Actions */}
        {process.env.NODE_ENV === 'development' && (
          <DevToolbar />
        )}
        
      </div>
    </header>
  );
};

export default V3Header;

const DevToolbar: React.FC = () => {
  const [wsBadgeEnabled, setWsBadgeEnabled] = useState<boolean>(false);
  useEffect(() => {
    setWsBadgeEnabled(localStorage.getItem('wsDebugBadge') === '1');
  }, []);
  const toggleWsBadge = () => {
    const next = !wsBadgeEnabled;
    setWsBadgeEnabled(next);
    localStorage.setItem('wsDebugBadge', next ? '1' : '0');
  };
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggleWsBadge}
        className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${wsBadgeEnabled ? 'bg-[#EEF2FF] text-[#4342FF] border-[#DDE2FF]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        title="Toggle WebSocket debug badge"
      >
        WS Badge: {wsBadgeEnabled ? 'On' : 'Off'}
      </button>
    </div>
  );
};
